import { getChecklists } from 'api/checklists/checklistsAPI';

// Checklist type for patrol/scheduler checklists
const PATROL_CHECKLIST_TYPE = 'Patrol';

/**
 * Fetch global checklists (not tied to any customer/address)
 * These are available for all patrols regardless of location
 * @returns {Promise<Array>} Promise that resolves to an array of checklist objects
 */
export const fetchGlobalChecklists = async () => {
  // Global checklists have null customerID and null addressID
  // Note: getChecklists signature is (checklistType, customerID, addressID)
  const data = await getChecklists(PATROL_CHECKLIST_TYPE, null, null);

  // Filter to only include truly global checklists (no customer/address association)
  // and transform to consistent format
  return (data || [])
    .filter((checklist) => !checklist.CustomerID && !checklist.AddressID)
    .map((checklist) => ({
      id: checklist.ChecklistID,
      name: checklist.Name,
      customerId: null,
      addressId: null,
      group: 'Global',
      originalData: checklist,
    }));
};

/**
 * Fetch location-specific checklists for a customer and address
 * These are only available for patrols at the specific location
 * @param {number|string} customerId - The customer ID
 * @param {number|string} addressId - The address/location ID
 * @returns {Promise<Array>} Promise that resolves to an array of checklist objects
 */
export const fetchLocationChecklists = async (customerId, addressId) => {
  if (!customerId || !addressId) {
    return [];
  }

  // Note: getChecklists signature is (checklistType, customerID, addressID)
  const data = await getChecklists(PATROL_CHECKLIST_TYPE, customerId, addressId);

  // Filter to only include location-specific checklists and transform
  return (data || [])
    .filter((checklist) => checklist.CustomerID && checklist.AddressID)
    .map((checklist) => ({
      id: checklist.ChecklistID,
      name: checklist.Name,
      customerId: checklist.CustomerID,
      addressId: checklist.AddressID,
      group: 'Location',
      originalData: checklist,
    }));
};

/**
 * Fetch all available checklists for a patrol at a specific location
 * Combines global checklists and location-specific checklists
 * @param {number|string} customerId - The customer ID
 * @param {number|string} addressId - The address/location ID
 * @returns {Promise<{global: Array, location: Array}>} Promise that resolves to categorized checklists
 */
export const fetchChecklistsForLocation = async (customerId, addressId) => {
  const [globalChecklists, locationChecklists] = await Promise.all([
    fetchGlobalChecklists(),
    customerId && addressId ? fetchLocationChecklists(customerId, addressId) : Promise.resolve([]),
  ]);

  return {
    global: globalChecklists,
    location: locationChecklists,
  };
};

/**
 * Transform selected checklists to the format expected by the API
 * @param {Array} checklists - Array of checklist objects from the selector
 * @returns {Array} Array of checklist IDs in API format
 */
export const transformChecklistsForApi = (checklists) => {
  if (!checklists || !Array.isArray(checklists)) {
    return [];
  }
  return checklists.map((checklist) => ({
    ChecklistID: checklist.id || checklist.ChecklistID,
  }));
};

/**
 * Transform API checklist data to selector format
 * @param {Array} apiChecklists - Array of checklist objects from the API
 * @returns {Array} Array of checklist objects for the selector
 */
export const transformChecklistsFromApi = (apiChecklists) => {
  if (!apiChecklists || !Array.isArray(apiChecklists)) {
    return [];
  }
  return apiChecklists.map((checklist) => ({
    id: checklist.ChecklistID,
    name: checklist.Name,
    customerId: checklist.CustomerID,
    addressId: checklist.AddressID,
    group: checklist.CustomerID && checklist.AddressID ? 'Location' : 'Global',
  }));
};
